import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { resolveLegacyRedirect } from "@/lib/legacy-redirects";

/**
 * Catch-all route for legacy URLs from the previous Precision Pavers & Pool
 * Design website.
 *
 * This runs during SSR: a fresh request to a legacy URL resolves the
 * destination and throws a TRUE HTTP 301 permanent redirect (server-side —
 * never a client-side/JS/meta-refresh redirect). URLs with no legitimate
 * historical equivalent throw `notFound()` so they return a real HTTP 404
 * (no broad wildcard / soft-404 behavior).
 *
 * Tracking query strings (utm_*, gclid, fbclid, …) live in `search`, not in the
 * splat path, so they never affect matching and are dropped on redirect.
 *
 * More specific routes (existing current pages and the explicit legacy alias
 * routes) always match before this splat, so valid current URLs are untouched.
 */
export const Route = createFileRoute("/$")({
  beforeLoad: ({ params }) => {
    const splat = params._splat ?? "";
    const dest = resolveLegacyRedirect(`/${splat}`);
    if (dest) {
      // TRUE HTTP 301 permanent redirect, emitted server-side during SSR.
      // `Cache-Control: no-store` prevents edge/CDN caching of the redirect
      // so a future rule change can never be locked in by a cached 301.
      throw redirect({
        to: dest as any,
        statusCode: 301,
        replace: true,
        headers: { "cache-control": "no-store" },
      });
    }
    throw notFound();
  },
  component: () => null,
});
